import {
  offset,
  flip,
  shift,
  size,
  type UseFloatingOptions,
} from '@floating-ui/react';

/**
 * Directly under the commented words, aligned to where they start.
 *
 * One placement for every floating comment surface — the box a comment is written in
 * and the card it is read in. They are the same object a moment apart, and anchoring
 * them differently made writing a comment and reading it back feel like two features.
 *
 * Below rather than beside, because the panes this floats in have no margin to speak
 * of: the gutter placement this replaced needed room next to the prose, and where
 * there was none the card ended up over the words it was about. Where there *is* a
 * margin, the cards go in it and none of this is used.
 *
 * `size` is what keeps a long thread reachable. `flip` will put the card above the
 * line when there is no room below it, and a conversation taller than the space above
 * then runs off the top of the pane — with its first message, the one the thread is
 * about, being exactly the part that disappears. Capping the card to the room that
 * actually exists makes it scroll within the pane instead of past it.
 */
const VIEWPORT_PADDING_PX = 12;

export const commentBelowFloatingOptions: Partial<UseFloatingOptions> = {
  placement: 'bottom-start',
  middleware: [
    offset(8),
    flip({ padding: VIEWPORT_PADDING_PX }),
    shift({ padding: VIEWPORT_PADDING_PX }),
    size({
      padding: VIEWPORT_PADDING_PX,
      apply({ availableHeight, elements }) {
        elements.floating.style.maxHeight = `${Math.max(
          availableHeight,
          // Below this the card is too short to read a message in, and letting it
          // shrink further just produces a sliver. It overflows the pane instead,
          // which at that size means the pane is the problem.
          160
        )}px`;
      },
    }),
  ],
};
